/**
 * PLUG STAMP — Sneakergang NYC's signature graphic device.
 *
 * Octagonal brand seal in the spirit of Palace's Tri-Ferg or
 * Supreme's box logo — geometry-locked, infinitely scalable, deployed
 * everywhere the brand needs a recognition mark: favicon, PDP
 * brand badge, hero watermark, order-confirmation seal,
 * shipping-box sticker, footer mark.
 *
 * The perimeter text repeats `SNEAKERGANG · THE PLUG · BX · NEW YORK`
 * twice around a circular path inside the octagon. Center holds the
 * `BX` lockup. Inks via `currentColor` so the same component works on
 * both bone and ink backgrounds — just set the parent's text color.
 *
 * Server component. No client-side JS.
 *
 * Sizes anyone gives back useful output (favicon 16px → hero watermark
 * 480px). Below ~48px the perimeter text becomes illegible; the
 * `compact` variant strips the rim text and shows just the octagon +
 * BX wordmark, which is the right move for favicon and small chip
 * usage.
 */
import * as React from "react";

interface PlugStampProps {
  /** Pixel size (square). Default 200. Favicon = 32, PDP badge = 64, hero watermark = 320+. */
  size?: number;
  /** Pass through Tailwind classes for color/opacity/rotation/etc. */
  className?: string;
  /** Strip perimeter text for small sizes (favicons, tiny chips). */
  compact?: boolean;
  /** Accessible label. */
  ariaLabel?: string;
}

export function PlugStamp({
  size = 200,
  className,
  compact = false,
  ariaLabel = "Sneakergang NYC stamp",
}: PlugStampProps) {
  return (
    <svg
      viewBox="0 0 200 200"
      width={size}
      height={size}
      className={className}
      role="img"
      aria-label={ariaLabel}
      xmlns="http://www.w3.org/2000/svg"
    >
      <defs>
        {/* Circular path on which the perimeter text rides. Sits inside
            the inner octagon so the type never collides with the outer
            stroke. */}
        <path
          id="plug-stamp-arc"
          d="M 100,100 m -68,0 a 68,68 0 1,1 136,0 a 68,68 0 1,1 -136,0"
          fill="none"
        />
      </defs>

      {/* Outer octagon — thick stroke, flat-top orientation (postal-seal vibe). */}
      <polygon
        points="183,134 134,183 66,183 17,134 17,66 66,17 134,17 183,66"
        fill="none"
        stroke="currentColor"
        strokeWidth="3"
        strokeLinejoin="miter"
      />

      {/* Inner octagon — thin hairline, concentric, gives the "stamp" depth. */}
      <polygon
        points="170,128 128,170 72,170 30,128 30,72 72,30 128,30 170,72"
        fill="none"
        stroke="currentColor"
        strokeWidth="1"
        strokeOpacity="0.55"
        strokeLinejoin="miter"
      />

      {/* Perimeter text. Two repeats so the loop reads continuously from any
          starting position. Mono face + wide tracking = stamp-ish, not editorial. */}
      {!compact && (
        <text
          fill="currentColor"
          fontFamily='"JetBrains Mono", ui-monospace, monospace'
          fontSize="9"
          letterSpacing="2.4"
          fontWeight="500"
        >
          <textPath href="#plug-stamp-arc" startOffset="0%">
            SNEAKERGANG · THE PLUG · BX · NEW YORK · SNEAKERGANG · THE PLUG · BX · NEW YORK ·
          </textPath>
        </text>
      )}

      {/* Centre lockup — "BX" in heavy condensed display. The single anchor
          glyph that ties the stamp to Lorenzo's geographic identity. */}
      <g>
        <text
          x="100"
          y="115"
          textAnchor="middle"
          fontFamily='"Inter Tight", system-ui, sans-serif'
          fontSize="46"
          fontWeight="900"
          fontStretch="75%"
          letterSpacing="-0.02em"
          fill="currentColor"
        >
          BX
        </text>
        {/* Tiny eyebrow above BX — only when not compact, because compact uses
            the BX as the whole mark. */}
        {!compact && (
          <text
            x="100"
            y="78"
            textAnchor="middle"
            fontFamily='"JetBrains Mono", ui-monospace, monospace'
            fontSize="7"
            letterSpacing="2.5"
            fill="currentColor"
            fillOpacity="0.7"
          >
            EST. 2026
          </text>
        )}
        {/* Tiny tagline below BX — only when not compact. */}
        {!compact && (
          <text
            x="100"
            y="138"
            textAnchor="middle"
            fontFamily='"JetBrains Mono", ui-monospace, monospace'
            fontSize="7"
            letterSpacing="2.5"
            fill="currentColor"
            fillOpacity="0.7"
          >
            THE PLUG
          </text>
        )}
      </g>

      {/* Small dot markers at the 4 octagon corners (NW/NE/SW/SE) — anchors that
          read as the metal-die nubs on a real postal stamp. Skip in compact. */}
      {!compact && (
        <g fill="currentColor">
          <circle cx="100" cy="32" r="1.5" />
          <circle cx="100" cy="168" r="1.5" />
          <circle cx="32" cy="100" r="1.5" />
          <circle cx="168" cy="100" r="1.5" />
        </g>
      )}
    </svg>
  );
}
